import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import BrowserHelper from 'src/app/helpers/browser-helper';
import { Project } from 'src/app/model/project/project';
import { SubProject } from 'src/app/model/project/sub-project';
import {
  ProjectDisplayObservableService,
  TourConfigurationChangedEvent,
} from 'src/app/services/project-display-observable.service';
import { ProjectPlacement } from '../../../model/project/projectPlacement';
import { StringHelper } from '../../../helpers/string-helper';
import { ActivatedRoute } from '@angular/router';
import { RequestService } from 'src/app/services/request.service';
import ProjectHelper from 'src/app/helpers/project-helper';
import { CollectionHelper } from 'src/app/helpers/collection-helper';
import { GeneralHelper } from 'src/app/helpers/general-helper';
import DateHelper from 'src/app/helpers/date-helper';
import { User } from 'src/app/model/user';
import { SubscriptionWithDestroyTriggerComponent } from 'src/app/subscription-helpers/subscription-with-destroy-trigger.component';
import { Subject } from 'rxjs';
import { EmployeeChoicesService } from 'src/app/services/employee-choices.service';
import Employee from 'src/app/model/product/employee';
import { AssignSubProjectToTourControllerRequestService } from 'src/app/pages/immo-ip/tour/tour-page/service/assign-sub-project-to-tour-controller-request.service';
import moment from 'moment';

@Component({
  selector: 'appointment-collapse',
  templateUrl: './appointment-collapse.component.html',
  styleUrls: ['./appointment-collapse.component.scss'],
})
export class AppointmentCollapseComponent
  extends SubscriptionWithDestroyTriggerComponent
  implements OnInit, OnDestroy
{
  @Input()
  project: Project;

  @Input()
  subProject: SubProject;

  @Input()
  subProjectTitle: string;

  @Input()
  client = true;

  @Output()
  public save = new EventEmitter();

  appointmentCollapsed = true;
  safari: boolean;

  appointmentSpecificHour: boolean;
  assignedToTour = false;
  executed = false;

  contactName = '';
  contactEmail = null;
  mailHtml: string;
  mailBodyText: string;
  keyMailTooltip: string;
  mailRegistrationChecked = false;
  togglingMailRegistration = false;

  tourDate: Date;
  tourPlacerUser: User;

  appointmentEmployeeOptions: Employee[];

  private mailHtmlInputChanged = new Subject<void>();

  subProjectTourInfo: TourConfigurationChangedEvent;

  constructor(
    private projectDisplayObservableService: ProjectDisplayObservableService,
    private route: ActivatedRoute,
    private requestService: RequestService,
    private employeeChoicesService: EmployeeChoicesService,
    private assignSubProjectToTourControllerRequestService: AssignSubProjectToTourControllerRequestService
  ) {
    super();
  }

  ngOnInit(): void {
    this.safari = BrowserHelper.isSafari();
    this.processAppointment();
    this.executed = ProjectHelper.EXECUTED_FOR_CLIENT_STATUSES.includes(
      this.subProject.status
    );
    this.calculateMailHtml();

    this.addSubscription(
      this.projectDisplayObservableService.getTourConfigurationChangedSubscribable(),
      (event: TourConfigurationChangedEvent) => {
        if (event != null && event.subProjectId === this.subProject.id) {
          this.assignedToTour = event.assignedToTour;
          this.tourDate = event.date;
          this.tourPlacerUser = event.placer != null ? event.placer.user : null;
          this.subProjectTourInfo = event;
          this.mailHtmlInputChanged.next();
        }
      }
    );

    this.initializeCalculateMailHttpSubscription();

    this.addSubscription(this.route.fragment, (fragment) => {
      if (fragment === 'appointment') {
        this.appointmentCollapsed = false;
      }
    });

    this.addSubscription(
      this.employeeChoicesService.getImmoAdminChoicesSubscribable(),
      (immoAdmins: Employee[]) => {
        this.appointmentEmployeeOptions = immoAdmins;
      }
    );

    this.employeeChoicesService.updateImmoAdminChoices();
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    if (this.mailHtmlInputChanged != null) {
      this.mailHtmlInputChanged.complete();
      this.mailHtmlInputChanged = null;
    }
  }

  compareEntities(o1: any, o2: any): boolean {
    return GeneralHelper.compareEntities(o1, o2);
  }

  private initializeCalculateMailHttpSubscription(): void {
    const triggerObservable = this.createObservableUntilDestroy(
      this.mailHtmlInputChanged,
      this.projectDisplayObservableService.getProjectContactsChangedSubscribable()
    );
    this.addSubscription(triggerObservable, () => this.calculateMailHtml());
  }

  private processAppointment(): void {
    if (this.subProject.projectPlacement == null) {
      return;
    }
    this.mailRegistrationChecked =
      this.subProject.projectPlacement != null &&
      this.subProject.projectPlacement.collectKeyMailSendRegistration != null;
    this.appointmentSpecificHour = this.isAppointmentSpecificHour(
      this.subProject.projectPlacement
    );
  }

  public getAppointmentTime(): string {
    if (this.subProject.projectPlacement.appointmentTime)
      return this.subProject.projectPlacement.appointmentTime
        .toString()
        .slice(0, 5);
    return null;
  }

  private isAppointmentSpecificHour(
    projectPlacement: ProjectPlacement
  ): boolean {
    if (projectPlacement.appointmentTime != null) {
      return true;
    }
    return StringHelper.isEmpty(projectPlacement.appointmentTimeOfDay)
      ? null
      : false;
  }

  appointmentSpecificHourChanged(): void {
    if (this.appointmentSpecificHour) {
      this.subProject.projectPlacement.appointmentTimeOfDay = null;
    } else if (this.appointmentSpecificHour === false) {
      this.subProject.projectPlacement.appointmentTime = null;
    }
    this.save.emit();
  }

  appointmentTimeChanged(value: Date): void {
    this.subProject.projectPlacement.appointmentTime = value;
    this.save.emit();
  }

  collectKeyExtraInfoChange(value: string) {
    this.subProject.projectPlacement.collectKeyExtraInfo = value;
    this.projectDisplayObservableService
      .getProjectPlacementVariableChangedSubscribable()
      .next();
    this.save.emit();
  }

  needAppointmentInfoChanged(value: string): void {
    this.subProject.projectPlacement.needAppointmentInfo = value;
    this.save.emit();
  }

  appointmentFollowUpChanged(value: string): void {
    this.subProject.projectPlacement.appointmentFollowUp = value;
    this.save.emit();
  }

  async appointmentReadyChanged() {
    if (this.subProject.projectPlacement.appointmentReady) {
      this.subProject.projectPlacement.appointmentFailure = false;
      if (
        (this.subProject.projectPlacement.appointment ||
          this.subProject.appointmentBlocksTourAssignment) &&
        this.subProject.tourItem != null &&
        this.subProjectTourInfo != null &&
        this.subProjectTourInfo.assignedToTour != null &&
        this.subProjectTourInfo.placer != null &&
        this.subProjectTourInfo.date != null
      ) {
        await this.assignSubProjectToTourControllerRequestService.assignSubProjectToTour(
          this.subProject.id,
          this.subProjectTourInfo.placer.id,
          moment(this.subProjectTourInfo.date),
          true
        );
        this.projectDisplayObservableService
          .getTourConfigurationChangedSubscribable()
          .next({
            assignedToTour: true,
            subProjectId: this.subProject.id,
            placer: this.subProjectTourInfo.placer,
            date: this.subProjectTourInfo.date,
            locked: true,
          });
      }
    }
    this.save.emit();
  }

  appointmentFailureChanged(): void {
    if (this.subProject.projectPlacement.appointmentFailure) {
      this.subProject.projectPlacement.appointmentReady = false;
    }
    this.save.emit();
  }

  appointmentBlocksTourAssignmentChanged(): void {
    if (
      this.subProject.appointmentBlocksTourAssignment &&
      !this.subProject.projectPlacement.appointment &&
      !this.subProject.projectPlacement.collectKey
    ) {
      this.subProject.projectPlacement.appointment = true;
    }
    this.save.emit();
  }

  collectKeyReminderSend(): void {
    this.togglingMailRegistration = true;
    const sendCollectKeyReminderObservable = this.requestService.post(
      !this.mailRegistrationChecked,
      '/subprojects/collectKeyReminderSend/' +
        this.subProject.projectPlacement.id
    );
    sendCollectKeyReminderObservable
      .toPromise()
      .then((result) => {
        this.subProject.projectPlacement.collectKeyMailSendRegistration =
          result;
      })
      .finally(() => (this.togglingMailRegistration = false));
  }

  private calculateMailContact(): void {
    if (this.subProject.subProjectContact != null) {
      this.calculateMailContactFromSubProjectContact();
    } else {
      this.calculateMailContactFromFirstProjectContact();
    }
  }

  private calculateMailContactFromSubProjectContact(): void {
    if (this.subProject.subProjectContact.contact != null) {
      this.contactName = this.subProject.subProjectContact.contact.firstName;
      this.contactEmail = CollectionHelper.isPopulated(
        this.subProject.subProjectContact.contact.emails
      )
        ? this.subProject.subProjectContact.contact.emails[0].email
        : null;
    } else {
      this.contactName = null;
      this.contactEmail = null;
    }
  }

  private calculateMailContactFromFirstProjectContact(): void {
    const firstProjectContact = CollectionHelper.isPopulated(
      this.project.projectContacts
    )
      ? this.project.projectContacts[0]
      : null;
    if (firstProjectContact !== null) {
      this.contactName = firstProjectContact.contact.firstName;
      this.contactEmail = CollectionHelper.isPopulated(
        firstProjectContact.contact.emails
      )
        ? firstProjectContact.contact.emails[0].email
        : null;
    } else {
      this.contactName = null;
      this.contactEmail = null;
    }
  }

  private calculateMailHtml(): void {
    this.calculateMailContact();
    this.calculateKeyMailTooltip();
    if (
      this.contactEmail == null ||
      this.tourDate == null ||
      this.tourPlacerUser == null
    ) {
      return null;
    }
    const subject = ProjectHelper.getMailSubjectLine(this.project).replace(
      'Project',
      'Opdracht'
    );
    const formattedTourDate = DateHelper.formatDate(this.tourDate);
    const installerName = StringHelper.join(
      [this.tourPlacerUser.firstName, this.tourPlacerUser.lastName],
      ' '
    );
    const bodyText =
      'Beste ' +
      this.contactName +
      ',\n\n' +
      'Om de opdracht #' +
      this.project.id +
      ' uit te voeren, hebben wij een sleutel nodig.\n' +
      'We zouden deze graag op ' +
      formattedTourDate +
      ' bij u ophalen. Kan u deze klaarleggen voor onze installateur? (' +
      installerName +
      ')\n' +
      'Indien de sleutel niet aanwezig is, gelieve ons hiervan op de hoogte te brengen. ' +
      'Immo-afdeling: 03/825.40.56 / immo@inpress.be\n\n' +
      'Met vriendelijke groeten,\n\n' +
      'Het Inpress team\n\n' +
      '---\n\n' +
      'Cher ' +
      this.contactName +
      ',\n\n' +
      'Pour réaliser la mission #' +
      this.project.id +
      ", nous avons besoin d'une clé.\n" +
      'Nous souhaiterions venir la récupérer chez vous ' +
      formattedTourDate +
      '. Pourriez-vous la préparer pour notre installateur ? (' +
      installerName +
      ')\n' +
      "Si la clé n'est pas disponible, merci de nous en informer. " +
      'Département immobilier : 03/825.40.56 / immo@inpress.be\n\n' +
      'Cordialement,\n\n' +
      "L'équipe Inpress";
    this.mailBodyText = bodyText;
    this.mailHtml =
      'mailto:' +
      this.contactEmail +
      '?Subject= ' +
      subject +
      '&body=' +
      encodeURIComponent(bodyText);
  }

  copyEmailBodyToClipboard(): void {
    if (this.mailBodyText) {
      navigator.clipboard
        .writeText(this.mailBodyText)
        .then(() => {
          console.log('Email body copied to clipboard');
        })
        .catch((err) => {
          console.error('Failed to copy text: ', err);
        });
    }
  }

  calculateKeyMailTooltip(): void {
    let errMsg = '';
    if (this.contactEmail == null) {
      errMsg = ', geen contact e-mail gevonden';
    } else if (this.tourDate == null) {
      errMsg = ', geen toer datum geregistreerd';
    } else if (this.tourPlacerUser) {
      errMsg = ', geen installateur geregistreerd';
    }
    this.keyMailTooltip = 'Kan mail niet opstellen' + errMsg;
  }
}
